// Page scanner: turns a photo of a paper into a clean black and white page, in the phone's browser. Finds the paper, straightens it,
// and lifts the ink off the shadows. The server packs the pages into one light PDF (scanpdf.js). Everything but the two helpers at the
// bottom is plain math on numbers, so it runs and tests without a canvas.
const LONG_SIDE = 2200;   // a letter page at 200 dpi
const PHOTO_SIDE = 3000;
const FIND_SIDE = 420;

export const toGray = (rgba, w, h) => { const g = new Uint8Array(w * h); for (let i = 0; i < w * h; i += 1) g[i] = (rgba[i * 4] * 77 + rgba[i * 4 + 1] * 150 + rgba[i * 4 + 2] * 29) >> 8; return g; };

// The brightness that best splits an image in two. Pure.
export function otsu(g) {
  const hist = new Array(256).fill(0); for (let i = 0; i < g.length; i += 1) hist[g[i]] += 1;
  let sum = 0; for (let i = 0; i < 256; i += 1) sum += i * hist[i];
  let sumB = 0; let wB = 0; let best = 0; let cut = 127;
  for (let t = 0; t < 256; t += 1) {
    wB += hist[t]; if (!wB) continue;
    const wF = g.length - wB; if (!wF) break;
    sumB += t * hist[t];
    const between = wB * wF * (sumB / wB - (sum - sumB) / wF) ** 2;
    if (between > best) { best = between; cut = t; }
  }
  return cut;
}

// The paper's four corners [top left, top right, bottom right, bottom left]: the biggest bright shape, if it looks like a page. null when
// the page isn't clear (white paper on a white desk, or it fills the photo). Pure.
export function findPage(g, w, h) {
  const cut = otsu(g);
  const seen = new Uint8Array(w * h);
  let best = null;
  for (let s = 0; s < w * h; s += 1) {
    if (seen[s] || g[s] <= cut) continue;
    seen[s] = 1;
    const stack = [s]; const shape = { n: 0, tl: [Infinity, 0, 0], tr: [-Infinity, 0, 0], br: [-Infinity, 0, 0], bl: [Infinity, 0, 0] };
    while (stack.length) {
      const p = stack.pop(); const x = p % w; const y = (p - x) / w;
      shape.n += 1;
      if (x + y < shape.tl[0]) shape.tl = [x + y, x, y];
      if (x + y > shape.br[0]) shape.br = [x + y, x, y];
      if (x - y > shape.tr[0]) shape.tr = [x - y, x, y];
      if (x - y < shape.bl[0]) shape.bl = [x - y, x, y];
      for (const q of [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, y > 0 ? p - w : -1, y < h - 1 ? p + w : -1]) {
        if (q >= 0 && !seen[q] && g[q] > cut) { seen[q] = 1; stack.push(q); }
      }
    }
    if (!best || shape.n > best.n) best = shape;
  }
  if (!best) return null;
  const share = best.n / (w * h);
  if (share < 0.15 || share > 0.97) return null;
  return [best.tl, best.tr, best.br, best.bl].map(([, x, y]) => ({ x, y }));
}

// How big the straightened page comes out. Pure.
export function pageSize(q) {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const w = (d(q[0], q[1]) + d(q[3], q[2])) / 2; const h = (d(q[0], q[3]) + d(q[1], q[2])) / 2;
  const k = Math.min(LONG_SIDE, Math.max(w, h) * 1.4) / Math.max(w, h);
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

// Maps the output rectangle onto the paper in the photo. Pure.
export function mapTo(q, w, h) {
  const from = [[0, 0], [w, 0], [w, h], [0, h]];
  const a = []; 
  from.forEach(([x, y], i) => {
    const { x: u, y: v } = q[i];
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u], [0, 0, 0, x, y, 1, -v * x, -v * y, v]);
  });
  for (let c = 0; c < 8; c += 1) {
    let top = c; for (let r = c + 1; r < 8; r += 1) if (Math.abs(a[r][c]) > Math.abs(a[top][c])) top = r;
    [a[c], a[top]] = [a[top], a[c]];
    for (let r = 0; r < 8; r += 1) { if (r === c) continue; const f = a[r][c] / a[c][c]; for (let k = c; k < 9; k += 1) a[r][k] -= f * a[c][k]; }
  }
  const m = a.map((r, i) => r[8] / r[i]);
  return (x, y) => { const d = m[6] * x + m[7] * y + 1; return [(m[0] * x + m[1] * y + m[2]) / d, (m[3] * x + m[4] * y + m[5]) / d]; };
}

// The straightened page. Pure.
export function straighten(g, sw, sh, q, w, h) {
  const at = mapTo(q, w, h); const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const [u, v] = at(x + 0.5, y + 0.5);
      const x0 = Math.max(0, Math.min(sw - 2, Math.floor(u))); const y0 = Math.max(0, Math.min(sh - 2, Math.floor(v)));
      const fx = Math.max(0, Math.min(1, u - x0)); const fy = Math.max(0, Math.min(1, v - y0));
      const i = y0 * sw + x0;
      out[y * w + x] = (g[i] * (1 - fx) + g[i + 1] * fx) * (1 - fy) + (g[i + sw] * (1 - fx) + g[i + sw + 1] * fx) * fy;
    }
  }
  return out;
}

// Black ink on white: each pixel against the paper around it, so shadows and uneven light go away and faint pen stays. Pure.
export function inkOnly(g, w, h) {
  const sum = new Float64Array((w + 1) * (h + 1));
  for (let y = 0; y < h; y += 1) { let row = 0; for (let x = 0; x < w; x += 1) { row += g[y * w + x]; sum[(y + 1) * (w + 1) + x + 1] = sum[y * (w + 1) + x + 1] + row; } }
  const r = Math.max(8, Math.round(Math.max(w, h) / 40)); const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const y0 = Math.max(0, y - r); const y1 = Math.min(h, y + r + 1);
    for (let x = 0; x < w; x += 1) {
      const x0 = Math.max(0, x - r); const x1 = Math.min(w, x + r + 1);
      const mean = (sum[y1 * (w + 1) + x1] - sum[y0 * (w + 1) + x1] - sum[y1 * (w + 1) + x0] + sum[y0 * (w + 1) + x0]) / ((x1 - x0) * (y1 - y0));
      const v = g[y * w + x];
      out[y * w + x] = v < mean * 0.86 && mean - v > 14 ? 0 : 255;
    }
  }
  return out;
}

// A quarter turn clockwise, n times. Pure.
export function turn(px, w, h, n = 1) {
  let cur = { px, w, h };
  for (let t = 0; t < ((n % 4) + 4) % 4; t += 1) {
    const out = new Uint8Array(cur.w * cur.h);
    for (let y = 0; y < cur.h; y += 1) for (let x = 0; x < cur.w; x += 1) out[x * cur.h + (cur.h - 1 - y)] = cur.px[y * cur.w + x];
    cur = { px: out, w: cur.h, h: cur.w };
  }
  return cur;
}

// The whole photo, a little inside the edges, for when the paper can't be found. Pure.
export const wholePhoto = (w, h) => [{ x: w * 0.05, y: h * 0.05 }, { x: w * 0.95, y: h * 0.05 }, { x: w * 0.95, y: h * 0.95 }, { x: w * 0.05, y: h * 0.95 }];

const paint = (img, w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(img, 0, 0, w, h); return { c, x }; };

// A photo from the camera → ready for the corners.
export async function loadPhoto(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const long = Math.max(img.naturalWidth, img.naturalHeight);
    const k = Math.min(1, PHOTO_SIDE / long);
    const w = Math.round(img.naturalWidth * k); const h = Math.round(img.naturalHeight * k);
    const big = paint(img, w, h);
    const f = Math.min(1, FIND_SIDE / long); const fw = Math.round(img.naturalWidth * f); const fh = Math.round(img.naturalHeight * f);
    const small = paint(img, fw, fh);
    const found = findPage(toGray(small.x.getImageData(0, 0, fw, fh).data, fw, fh), fw, fh);
    const corners = found ? found.map((p) => ({ x: (p.x * w) / fw, y: (p.y * h) / fh })) : wholePhoto(w, h);
    return { url: big.c.toDataURL('image/jpeg', 0.7), w, h, gray: toGray(big.x.getImageData(0, 0, w, h).data, w, h), corners, found: !!found };
  } finally { URL.revokeObjectURL(url); }
}

// Corners → the finished page: a PNG to upload and to show.
export function finishPage(photo, corners, quarterTurns = 0) {
  const size = pageSize(corners);
  const flat = straighten(photo.gray, photo.w, photo.h, corners, size.w, size.h);
  const { px, w, h } = turn(inkOnly(flat, size.w, size.h), size.w, size.h, quarterTurns);
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const x = c.getContext('2d'); const im = x.createImageData(w, h);
  for (let i = 0; i < w * h; i += 1) { im.data[i * 4] = px[i]; im.data[i * 4 + 1] = px[i]; im.data[i * 4 + 2] = px[i]; im.data[i * 4 + 3] = 255; }
  x.putImageData(im, 0, 0);
  const url = c.toDataURL('image/png');
  return { url, dataBase64: url.split(',')[1], w, h };
}
